import { useState } from "react";
import { t, tx } from "../i18n";
import { cap, fmtDateTime, fmtInr } from "../ui/format";
import { editFields, type Choice } from "./edit-core";
import { resolveConflict, sendAgainOnCurrent, type OutboxItem } from "./outbox";

/*
 * Wave 7 (docs/implemented-offline7.md): what the outbox shows when something made offline met a change on the server.
 *   an edit      each field: what it was, what you typed, what the server has now. Keep mine, keep the server's, or choose
 *                field by field (a field only the server changed is always kept; one only you changed is yours unless you say)
 *   an issue     the draft changed: look, then issue it on the draft as it is now (the server flags totals that differ)
 *   an approval  decided by someone else, or changed: look, then decide again on what is there now, or discard it
 *   flagged      an invoice issued offline whose total the server worked out differently: issued, with both figures
 * Wave 8 (docs/implemented-offline8.md):
 *   refused      an invoice issued offline that the server refused: its number is used on the device, refused, never reused,
 *                and the person is told what to do about the copy the customer may have seen
 *   a payment    on an onboarding instalment that was paid meanwhile or is now due at another amount: what is due now, and
 *                record it on that once the person has looked
 * Wave 9 (docs/implemented-offline9.md):
 *   fixable      an invoice issued offline refused for a reason this device can fix: fix it and send it again with the same
 *                number until the block's end
 */

type Rec = Record<string, unknown>;

function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? t("Yes") : t("No");
  // Wave 9: a list of words (tags, other emails) shows the words; a list of lines, how many.
  if (Array.isArray(v) && v.length && v.every((x) => typeof x === "string" || typeof x === "number")) return v.join(", ");
  if (Array.isArray(v)) return v.length === 1 ? t("1 line") : t("{n} lines", { n: v.length });
  if (typeof v === "object") return Object.values(v as Rec).filter((x) => x !== null && x !== "" && typeof x !== "object").join(", ") || "—";
  return String(v);
}

const name = (key: string) => (key === "hours" ? t("Time") : tx(cap(key.replaceAll("_", " "))));

/** A conflicted edit, field by field. */
export function EditConflict({ item, busy, run }: { item: OutboxItem; busy: boolean; run: (fn: () => Promise<void>) => void }) {
  const fields = editFields(item);
  const [pick, setPick] = useState<Record<string, Choice>>(() => Object.fromEntries(fields.filter((f) => f.mineChanged).map((f) => [f.key, "mine" as Choice])));
  const clashes = fields.filter((f) => f.clash).length;
  return (
    <div className="pwa-conflict">
      <p className="pwa-error" role="alert">
        {item.error}
      </p>
      <table className="pwa-conflict__table">
        <thead>
          <tr>
            <th>{t("Field")}</th>
            <th>{t("Before")}</th>
            <th>{t("Yours")}</th>
            <th>{t("Now on the server")}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.key} className={f.clash ? "is-clash" : undefined}>
              <td data-label={t("Field")}>{name(f.key)}</td>
              <td data-label={t("Before")}>{show(f.base)}</td>
              <td data-label={t("Yours")}>
                {f.mineChanged ? (
                  <label className="pwa-pick">
                    <input type="radio" name={`${item.id}:${f.key}`} checked={pick[f.key] === "mine"} onChange={() => setPick({ ...pick, [f.key]: "mine" })} /> {show(f.mine)}
                  </label>
                ) : (
                  <small>{t("unchanged")}</small>
                )}
              </td>
              <td data-label={t("Now on the server")}>
                {f.mineChanged ? (
                  <label className="pwa-pick">
                    <input type="radio" name={`${item.id}:${f.key}`} checked={pick[f.key] === "theirs"} onChange={() => setPick({ ...pick, [f.key]: "theirs" })} /> {show(f.theirs)}
                  </label>
                ) : (
                  <span>
                    {show(f.theirs)} <small>{t("(kept)")}</small>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <small>{clashes ? t("{n} changed in both places: choose which to keep.", { n: clashes }) : t("Nothing you changed was changed on the server too, but the record moved on: confirm to send yours.")}</small>
      <span className="pwa-actions">
        <button type="button" className="button button--primary button--sm" disabled={busy} onClick={() => run(() => resolveConflict(item.id, pick))}>
          {t("Apply my choices")}
        </button>
        <button type="button" className="button button--secondary button--sm" disabled={busy} onClick={() => run(() => resolveConflict(item.id, "mine"))}>
          {t("Keep mine")}
        </button>
        <button type="button" className="button button--secondary button--sm" disabled={busy} onClick={() => window.confirm(t("Keep the server's copy? What you typed offline will be dropped.")) && run(() => resolveConflict(item.id, "theirs"))}>
          {t("Keep the server's")}
        </button>
      </span>
    </div>
  );
}

/** An issue or approval that met a change: what is there now, and whether it can be sent again on it. */
export function ActionConflict({ item, busy, online, run }: { item: OutboxItem; busy: boolean; online: boolean; run: (fn: () => Promise<void>) => void }) {
  const cur = (item.conflict?.current ?? null) as Rec | null;
  const entries = Array.isArray(cur?.entries) ? (cur.entries as Rec[]) : null;
  // Still open to a decision: a draft not yet issued, a request still pending, time entries still waiting.
  // Wave 8: a payment on an onboarding instalment: the instalment as it is now (the onboarding is in `current`).
  const inst = item.kind === "payment" && Array.isArray(cur?.installments) ? ((cur.installments as Rec[]).find((x) => x.id === item.path.split("/")[5]) ?? null) : null;
  const open = item.kind === "payment" ? Boolean(inst && inst.status !== "paid" && inst.status !== "cancelled" && cur?.status !== "cancelled") : item.kind === "issue" ? cur?.status === "draft" : entries ? entries.every((e) => e.status === "pending") : cur?.status === "pending";
  const facts =
    item.kind === "payment"
      ? inst
        ? t("{label} now: {status}, {amount}.", { label: tx(show(inst.label)), status: tx(show(inst.status)).replaceAll("_", " "), amount: fmtInr(String(inst.amount ?? ""), true) })
        : t("This payment is no longer on the onboarding.")
      : item.kind === "issue" && cur
      ? t("The draft now: {total}, {lines}, {customer}.", { total: fmtInr(String(cur.total ?? ""), true), lines: show(cur.lines ?? []), customer: show(cur.customer_name) })
      : entries
        ? entries.map((e) => t("{date}: {hours} h, {status}", { date: show(e.date), hours: show(e.hours), status: tx(show(e.status)) })).join(" · ")
        : cur
          ? t("Now: {status}, {amount}{note}", { status: tx(show(cur.status)), amount: show(cur.amount), note: cur.request_note ? ` · ${String(cur.request_note)}` : "" })
          : "";
  return (
    <div className="pwa-conflict">
      <p className="pwa-error" role="alert">
        {item.error}
      </p>
      {facts && <small>{facts}</small>}
      {open && item.conflict && (
        <span className="pwa-actions">
          <button type="button" className="button button--primary button--sm" disabled={busy || !online} onClick={() => run(() => sendAgainOnCurrent(item.id))}>
            {item.kind === "issue" ? t("Issue it as it is now") : item.kind === "payment" ? t("Record it at the amount due now") : t("Decide again on this")}
          </button>
        </span>
      )}
    </div>
  );
}

/** Issued, but the server's figures differ from what this device showed. */
export function IssueFlagged({ item }: { item: OutboxItem }) {
  const f = item.flag;
  if (!f) return null;
  return (
    <p className="pwa-error" role="status">
      {t("Issued. This device showed {shown}{tax}; the server worked out {server} (GST {stax}). The invoice carries the server's figures: check it.", {
        shown: fmtInr(f.shown_total, true),
        tax: f.shown_tax ? ` (GST ${fmtInr(f.shown_tax, true)})` : "",
        server: fmtInr(f.server_total, true),
        stax: fmtInr(f.server_tax, true),
      })}
    </p>
  );
}

/**
 * Wave 8: an invoice issued offline that the server refused. Its number was printed on this device, so it is spent: the
 * server marked it used on the device, refused, and it never goes on another invoice. What to do about it.
 */
export function RefusedIssue({ item }: { item: OutboxItem }) {
  const r = item.refused;
  if (!r) return null;
  return (
    <div className="pwa-conflict">
      <p className="pwa-error" role="alert">
        {item.error}
      </p>
      <small>{t("What to do: if the customer saw or was given {number}, tell them it is void. Then issue the invoice again online (it gets a new number), or ask someone who may issue invoices to. {number} stays in the numbering report with the reason.", { number: r.number })}</small>
    </div>
  );
}

/**
 * Wave 9: an invoice issued offline that the server refused for a reason this device can fix. Its number stays reserved to
 * the device until the block's end (by the server's clock): fix the invoice and send it again with the same number. After
 * that it is spent, as a refused one is.
 */
export function FixableIssue({ item, now, busy, onFix, onSend }: { item: OutboxItem; now: number; busy: boolean; onFix: () => void; onSend: () => void }) {
  const f = item.fixable;
  if (!f) return null;
  const over = Boolean(f.until && Date.parse(f.until) <= now);
  return (
    <div className="pwa-conflict">
      <p className="pwa-error" role="alert">
        {t("The server refused it: {reason}", { reason: f.reason })}
      </p>
      {over ? (
        <small>{t("{number} was kept for this invoice until {until}, and that time is over, so it is now used on this device and refused. If the customer saw {number}, tell them it is void, then issue the invoice again online.", { number: f.number, until: fmtWhen(f.until) })}</small>
      ) : (
        <>
          <small>{t("Fix it and send it again: {number} stays reserved to this device for this invoice until {until}.", { number: f.number, until: fmtWhen(f.until) })}</small>
          <span className="pwa-actions">
            <button type="button" className="button button--primary button--sm" disabled={busy} onClick={onFix}>
              {t("Fix the invoice")}
            </button>
            <button type="button" className="button button--secondary button--sm" disabled={busy} onClick={onSend}>
              {t("Send again as {number}", { number: f.number })}
            </button>
          </span>
        </>
      )}
    </div>
  );
}

const fmtWhen = (iso: string | null) => (iso ? fmtDateTime(iso) : t("the block's end"));
